import { useState, useEffect } from 'react';
import api from '../../api';

export default function ChannelSelect({ guildId, value, onChange, placeholder = 'Select a channel...' }) {
  const [grouped, setGrouped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!guildId) return;
    api.get(`/api/discord/${guildId}/channels`)
      .then(res => setGrouped(res.data.grouped || []))
      .catch(err => setError('Could not load channels'))
      .finally(() => setLoading(false));
  }, [guildId]);

  // Find display name for current value
  const allChannels = grouped.flatMap(g => g.channels || []);
  const selected = allChannels.find(c => c.id === value);

  if (loading) return (
    <div style={{
      padding: '10px 12px', border: '2px solid var(--border)', borderRadius: '10px',
      fontSize: '14px', color: 'var(--text-muted)', background: 'var(--bg-card)',
    }}>
      Loading channels...
    </div>
  );

  if (error) return (
    <div style={{
      padding: '10px 12px', border: '2px solid var(--border)', borderRadius: '10px',
      fontSize: '14px', color: 'var(--danger)', background: 'var(--bg-card)',
    }}>
      {error}
    </div>
  );

  return (
    <div style={{ position: 'relative' }}>
      {/* Trigger */}
      <div
        onClick={() => setOpen(!open)}
        style={{
          padding: '10px 12px', border: `2px solid ${open ? 'var(--accent-primary)' : 'var(--border)'}`,
          borderRadius: '10px', fontSize: '14px', fontFamily: 'var(--font-body)',
          background: 'var(--bg-card)', color: selected ? 'var(--text-primary)' : 'var(--text-muted)',
          cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          transition: 'border-color 0.2s', userSelect: 'none',
        }}
      >
        <span>{selected ? `# ${selected.name}` : placeholder}</span>
        <span style={{ fontSize: '12px', color: 'var(--text-muted)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
      </div>

      {/* Dropdown */}
      {open && (
        <>
          {/* Backdrop */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 998 }}
            onClick={() => setOpen(false)}
          />
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0,
            background: 'var(--bg-card)', border: '2px solid var(--border)',
            borderRadius: '10px', zIndex: 999, marginTop: '4px',
            boxShadow: 'var(--shadow)', maxHeight: '280px', overflowY: 'auto',
          }}>
            {/* Clear option */}
            <div
              onClick={() => { onChange(''); setOpen(false); }}
              style={{
                padding: '10px 14px', cursor: 'pointer', fontSize: '13px',
                color: 'var(--text-muted)', borderBottom: '1px solid var(--border)',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              — None —
            </div>

            {grouped.map(category => (
              <div key={category.id || 'none'}>
                {/* Category header */}
                <div style={{
                  padding: '8px 14px 4px',
                  fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '0.5px', color: 'var(--text-muted)',
                  background: 'var(--bg-secondary)',
                }}>
                  {category.name}
                </div>
                {/* Channels in category */}
                {(category.channels || []).map(channel => (
                  <div
                    key={channel.id}
                    onClick={() => { onChange(channel.id); setOpen(false); }}
                    style={{
                      padding: '8px 14px 8px 24px', cursor: 'pointer',
                      fontSize: '14px', color: value === channel.id ? 'var(--accent-primary)' : 'var(--text-primary)',
                      background: value === channel.id ? 'var(--accent-soft)' : 'transparent',
                      display: 'flex', alignItems: 'center', gap: '8px',
                      transition: 'background 0.15s',
                      fontWeight: value === channel.id ? 700 : 400,
                    }}
                    onMouseEnter={e => { if (value !== channel.id) e.currentTarget.style.background = 'var(--bg-secondary)'; }}
                    onMouseLeave={e => { if (value !== channel.id) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ color: 'var(--text-muted)', fontSize: '15px' }}>#</span>
                    {channel.name}
                    {value === channel.id && <span style={{ marginLeft: 'auto', fontSize: '12px' }}>✓</span>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
